import { LanguageVariantsSection } from "@/components/language-variants-section";
import { BuilderSection } from "@/components/studio/builder-section";
import { BuilderShell } from "@/components/studio/builder-shell";
import { IntentTaggingSection } from "@/components/studio/intent-tagging-section";
import { RefinementSection } from "@/components/studio/refinement-section";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useUpload } from "@/hooks/use-upload";
import { ContentBlogService } from "@/lib/api/blog.service";
import { categoryService } from "@/lib/api/categories.service";
import { coachService } from "@/lib/api/coachService";
import { ContentTagService } from "@/lib/api/content.tag.service";
import { useAuth } from "@/lib/auth";
import { queryClient } from "@/lib/queryClient";
import type { Category, Coach, TagGroup, TagOption } from "@/lib/schema";
import {
  CONTENT_TYPE_CONFIG,
  SUPPORTED_LANGUAGES,
} from "@/lib/studio-constants";
import { validateImageUrl } from "@/lib/image-url-validation";
import { validateThumbnailFile } from "@/lib/thumbnail-validation";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { BlogVariantFields } from "./blog-variant-fields";

const blogConfig = CONTENT_TYPE_CONFIG.blog;
const ELEVATED_ROLES = [
  "admin",
  "super_admin",
  "coach_manager",
  "content_manager",
];

function validateBodyImageUrls(body: string | null): string | null {
  if (!body) return null;
  let blocks: unknown;
  try {
    blocks = JSON.parse(body);
  } catch {
    return null;
  }
  if (!Array.isArray(blocks)) return null;
  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i] as { type?: string; content?: unknown };
    if (block?.type !== "image") continue;
    const content = typeof block.content === "string" ? block.content : "";
    const result = validateImageUrl(content);
    if (!result.valid) {
      return `Image block ${i + 1}: ${result.error}`;
    }
  }
  return null;
}

interface LanguageVariantDraft {
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription: string;
  localizedBody: string | null;
  thumbnailUrl: string | null;
}

export default function BlogCreatePage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { uploadFile, isUploading } = useUpload();
  const { user } = useAuth();
  const isCoachOnly =
    !!user?.roles?.includes("coach") &&
    !user.roles.some((r) => ELEVATED_ROLES.includes(r));
  const selfCoachId = user?.id ?? null;

  const [categoryId, setCategoryId] = useState("");
  const [selectedTagOptionIds, setSelectedTagOptionIds] = useState<string[]>(
    [],
  );
  const [authorCoachId, setAuthorCoachId] = useState<string | null>(null);
  const [internalNotes, setInternalNotes] = useState("");
  const [variants, setVariants] = useState<LanguageVariantDraft[]>([
    {
      languageCode: "en",
      isDefault: true,
      localizedTitle: "",
      localizedDescription: "",
      localizedBody: null,
      thumbnailUrl: null,
    },
  ]);
  const [activeVariantIndex, setActiveVariantIndex] = useState(0);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  );

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/content-category"],
    queryFn: () => categoryService.findAll(),
  });

  const { data: tagGroups = [] } = useQuery<TagGroup[]>({
    queryKey: ["/content/tag-groups"],
    queryFn: () => ContentTagService.getTagGroups(),
  });

  const { data: tagOptions = [] } = useQuery<TagOption[]>({
    queryKey: ["/content/tag-options"],
    queryFn: () => ContentTagService.getTagOptions(),
  });

  const { data: coaches = [] } = useQuery<Coach[]>({
    queryKey: ["/api/coaches"],
    queryFn: () => coachService.getCoaches().then((r) => r.coaches),
    enabled: !isCoachOnly,
  });

  useEffect(() => {
    if (isCoachOnly && selfCoachId && !authorCoachId) {
      setAuthorCoachId(selfCoachId);
    }
  }, [isCoachOnly, selfCoachId, authorCoachId]);

  const createMutation = useMutation({
    mutationFn: async (submitForApproval: boolean) => {
      if (submitForApproval) {
        if (!categoryId)
          throw new Error(
            "Please select a category before submitting for approval.",
          );
        const defaultVar = variants.find((v) => v.isDefault);
        if (!defaultVar?.localizedTitle?.trim())
          throw new Error(
            "Please add a title to the default language variant before submitting.",
          );
        if (!defaultVar?.thumbnailUrl)
          throw new Error("Please upload a hero image before submitting.");
      } else if (!categoryId) {
        throw new Error("Please select a category to save this blog.");
      }

      for (const v of variants) {
        const bodyError = validateBodyImageUrls(v.localizedBody);
        if (bodyError) {
          throw new Error(
            `${v.languageCode.toUpperCase()} variant: ${bodyError}`,
          );
        }
      }
      const payload = {
        blog: {
          categoryId,
          tagOptionIds: selectedTagOptionIds,
          authorCoachId,
          internalNotes: internalNotes || null,
        },
        variants: variants.map((v) => ({
          languageCode: v.languageCode,
          isDefault: v.isDefault,
          localizedTitle: v.localizedTitle,
          localizedDescription: v.localizedDescription || null,
          localizedBody: v.localizedBody,
          thumbnailUrl: v.thumbnailUrl,
          translationStatus: v.isDefault ? "completed" : "pending",
        })),
      };

      const data = await ContentBlogService.create(payload);

      if (submitForApproval) {
        try {
          await ContentBlogService.submitForReview(data.id);
        } catch (submitErr: any) {
          const msg = submitErr?.message || "Submission failed";
          throw new Error(`Draft saved but could not submit: ${msg}`);
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/content-blogs"] });
      toast({ title: "Blog content created" });
      setLocation("/studio/blogs");
    },
    onError: (err: Error) => {
      queryClient.invalidateQueries({ queryKey: ["/content-blogs"] });
      toast({
        title: err.message || "Failed to create blog content",
        variant: "destructive",
      });
    },
  });

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "thumbnailUrl",
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = await validateThumbnailFile(file);
    if (!validation.valid) {
      toast({
        title: "Invalid thumbnail",
        description: validation.error,
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }
    const result = await uploadFile(file);
    if (result) {
      const updated = [...variants];
      updated[activeVariantIndex] = {
        ...updated[activeVariantIndex],
        [field]: result.objectPath,
      };
      setVariants(updated);
    }
  };

  const updateVariant = (field: keyof LanguageVariantDraft, value: any) => {
    const updated = [...variants];
    updated[activeVariantIndex] = {
      ...updated[activeVariantIndex],
      [field]: value,
    };
    setVariants(updated);
  };

  const addLanguageVariant = (langCode: string) => {
    if (variants.some((v) => v.languageCode === langCode)) return;
    setVariants([
      ...variants,
      {
        languageCode: langCode,
        isDefault: false,
        localizedTitle: "",
        localizedDescription: "",
        localizedBody: null,
        thumbnailUrl: null,
      },
    ]);
    setActiveVariantIndex(variants.length);
  };

  const confirmRemoveLanguageVariant = () => {
    if (pendingRemoveIndex === null) return;
    if (variants[pendingRemoveIndex].isDefault) return;
    if (variants.length <= 1) {
      toast({
        title: "At least one language variant must remain",
        variant: "destructive",
      });
      setPendingRemoveIndex(null);
      return;
    }
    const updated = variants.filter((_, i) => i !== pendingRemoveIndex);
    setVariants(updated);
    if (activeVariantIndex >= updated.length) {
      setActiveVariantIndex(Math.max(0, updated.length - 1));
    }
    setPendingRemoveIndex(null);
  };

  const setAsDefault = (index: number) => {
    if (index < 0 || index >= variants.length) return;
    const updated = variants.map((v, i) => ({ ...v, isDefault: i === index }));
    setVariants(updated);
    setActiveVariantIndex(index);
  };

  const toggleTagOption = (id: string) => {
    setSelectedTagOptionIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
    );
  };

  const getGroupOptions = (groupId: string) =>
    tagOptions.filter((to) => to.groupId === groupId);

  const availableLanguages = SUPPORTED_LANGUAGES.filter(
    (lang) => !variants.some((v) => v.languageCode === lang.code),
  );

  const activeVariant = variants[activeVariantIndex];

  return (
    <BuilderShell
      contentTypeConfig={blogConfig}
      mode="create"
      onSave={() => createMutation.mutate(false)}
      onSubmit={() => createMutation.mutate(true)}
      isSaving={createMutation.isPending}
      status="draft"
    >
      <IntentTaggingSection
        stepNumber={1}
        categories={categories}
        categoryId={categoryId}
        onCategoryChange={setCategoryId}
        tagGroups={tagGroups}
        getGroupOptions={getGroupOptions}
        selectedTagOptionIds={selectedTagOptionIds}
        onToggleTagOption={toggleTagOption}
      />

      <BuilderSection
        title="Content"
        stepNumber={2}
        description="Select the author for this blog"
        defaultOpen={true}
      >
        <div className="space-y-2">
          <Label>Author</Label>
          {isCoachOnly ? (
            <Input
              value={user?.name ?? ""}
              disabled
              data-testid="select-author-coach"
            />
          ) : (
            <Select
              value={authorCoachId || "none"}
              onValueChange={(val) =>
                setAuthorCoachId(val === "none" ? null : val)
              }
            >
              <SelectTrigger data-testid="select-author-coach">
                <SelectValue placeholder="Select author" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No author</SelectItem>
                {coaches.map((coach) => (
                  <SelectItem key={coach.id} value={coach.id}>
                    {coach.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </BuilderSection>

      <LanguageVariantsSection
        stepNumber={3}
        variants={variants}
        activeVariantIndex={activeVariantIndex}
        onActiveVariantChange={setActiveVariantIndex}
        availableLanguages={availableLanguages}
        onAddVariant={addLanguageVariant}
        onSetDefault={setAsDefault}
        pendingRemoveIndex={pendingRemoveIndex}
        onRequestRemove={setPendingRemoveIndex}
        onConfirmRemove={confirmRemoveLanguageVariant}
        onCancelRemove={() => setPendingRemoveIndex(null)}
      >
        {activeVariant && (
          <BlogVariantFields
            variant={activeVariant}
            onFieldChange={updateVariant}
            onFileUpload={handleFileUpload}
            isUploading={isUploading}
          />
        )}
      </LanguageVariantsSection>

      <RefinementSection
        stepNumber={4}
        internalNotes={internalNotes}
        onInternalNotesChange={setInternalNotes}
      />
    </BuilderShell>
  );
}
